/* eslint-disable no-fallthrough */
import React, { FC } from "react";
import _ from "lodash";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  ChartData,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import {
  AnswerOptions,
  Iteration,
  ListFeedbackResponseJsonItemHistory,
} from "typings/api/types";
import { chartColors } from "assets/styles/variables/colors";
import { Question, QuestionType } from "typings/app/types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PointElement
);

interface LineChartProps {
  data: ChartData<"line">;
  labelCallback?: (val: any, index: number) => any
  min?: string,
  max?: string
}
const LineChart: FC<LineChartProps> = ({ data, labelCallback, min = "0", max ="5" }) => {
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      yAxis: {
        min,
        max,
        ticks: {
          callback: labelCallback !== undefined ? labelCallback :  (value: any) => parseInt(value as string),
        },
      },
    },
    plugins: {
      decimation: {
        enabled: false,
      },
      legend: {
        position: "right" as const,
        display: false,
        maxWidth: 500,
      },
      title: {
        display: false,
        text: "Chart.js Bar Chart",
      },
    },
  };

  return (
    <div className="flex max-w-lg">
      <Line width={500} options={options} data={data} />
    </div>
  );
};

const PercentageLineChart: FC<LineChartProps> = ({ data }) => {
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      yAxis: {
        min: "0",
        max: "1",
        ticks: {
          callback: (value: any) => parseFloat(value) * 100 + "%",
        },
      },
    },
    plugins: {
      decimation: {
        enabled: false,
      },
      legend: {
        position: "right" as const,
        display: false,
        maxWidth: 500,
      },
      title: {
        display: false,
        text: "Chart.js Bar Chart",
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            let label = context.dataset.label || "";

            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y * 100 + "%";
            }
            return label;
          },
        },
      },
    },
  };

  return (
    <div className="flex max-w-lg">
      <Line width={500} options={options} data={data} />
    </div>
  );
};

interface BarChartProps {
  data: ChartData<"bar">;
}

const StackedBarChart: FC<BarChartProps> = ({ data }) => {
  const options = {
    responsive: false,
    maintainAspectRatio: true,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        position: "right" as const,
        maxWidth: 500,
      },
      title: {
        display: false,
        text: "Chart.js Bar Chart",
      },
    },
  };

  return (
    <div className="flex max-w-lg">
      <Bar width={500} options={options} data={data} />
    </div>
  );
};

interface AnswerHistoryProps {
  history?: ListFeedbackResponseJsonItemHistory;
  answerOptions: AnswerOptions[],
  questionType: string;
  iterations: { [key: number]: Iteration };
  labelSelector: (arg0: Iteration) => string;
}

const AnswerHistory: FC<AnswerHistoryProps> = ({
  history,
  questionType,
  iterations,
  answerOptions,
  labelSelector,
}) => {
  if (!history) return null;
  const chartLabels = Object.keys(history).map((key) =>
    labelSelector(iterations[parseInt(key)])
  );

  const renderChart = () => {
    switch (questionType) {
      case QuestionType.LIKERT_SCALE:
        return (
          <LineChart
            labelCallback={(val) => answerOptions[val-1]?.label || val}
            min="1"
            max={answerOptions.length === 4 ? "4" : "5" }
            data={{
              labels: chartLabels,
              datasets: [
                {
                  data: Object.values(history).map(
                    ({ AverageRating }) => AverageRating
                  ),
                  backgroundColor: Object.values(chartColors)[0],
                  borderColor: Object.values(chartColors)[0],
                },
              ],
            }}
          />
        );
      case QuestionType.STAR_RATING:
        return (
          <LineChart
            data={{
              labels: chartLabels,
              datasets: [
                {
                  data: Object.values(history).map(
                    ({ AverageRating }) => AverageRating
                  ),
                  backgroundColor: Object.values(chartColors)[0],
                  borderColor: Object.values(chartColors)[0],
                },
              ],
            }}
          />
        );
      case QuestionType.MULTI_SELECT:
      case QuestionType.SELECT:
        console.log({ history });
        let datasets: any[] = [];
        Object.values(history).forEach(({ AnswersCount }) => {
          Object.keys(AnswersCount).forEach((label, index) => {
            let found = datasets.find((p: any) => p.label === label);
            if (found !== undefined) {
              found.data.push(AnswersCount[label]);
            } else {
              datasets.push({
                label,
                data: [AnswersCount[label]],
                backgroundColor: Object.values(chartColors)[index],
              });
            }
          });
        });
        return (
          <StackedBarChart
            data={{
              labels: chartLabels,
              datasets,
            }}
          />
        );
      case QuestionType.THUMB_RATING:
        return (
          <PercentageLineChart
            data={{
              labels: chartLabels,
              datasets: [
                {
                  data: Object.values(history).map(
                    ({ AverageRating }) => AverageRating
                  ),
                  backgroundColor: Object.values(chartColors)[0],
                  borderColor: Object.values(chartColors)[0],
                },
              ],
            }}
          />
        );
      default:
        return null;
    }
  };

  return <div className="w-full">{renderChart()}</div>;
};

export default AnswerHistory;
